import { Link } from 'react-router-dom';

interface CtaAction {
  label: string;
  to?: string;
  href?: string;
  variant?: 'primary' | 'secondary';
}

interface PageHeroProps {
  eyebrow?: string;
  eyebrowIcon?: string;
  title: React.ReactNode;
  subtitle?: string;
  image: string;
  alt: string;
  breadcrumb?: { label: string; to?: string }[];
  ctas?: CtaAction[];
}

export default function PageHero({
  eyebrow,
  eyebrowIcon,
  title,
  subtitle,
  image,
  alt,
  breadcrumb,
  ctas = [],
}: PageHeroProps) {
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-foreground-950">
      <div className="absolute inset-0">
        <img
          src={image}
          alt={alt}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/40 to-foreground-950/85" />
      </div>

      <div className="relative z-10 w-full py-28 md:py-36">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          {breadcrumb && breadcrumb.length > 0 && (
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-white/40 mb-6">
              {breadcrumb.map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <i className="ri-arrow-right-s-line text-white/25"></i>}
                  {item.to ? (
                    <Link to={item.to} className="hover:text-white/80 transition-colors whitespace-nowrap">
                      {item.label}
                    </Link>
                  ) : (
                    <span className="text-white/70 font-medium whitespace-nowrap">{item.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}

          {eyebrow && (
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white/80 text-[11px] font-medium px-4 py-1.5 rounded-full mb-6 uppercase tracking-[0.15em] backdrop-blur-sm">
              {eyebrowIcon && <i className={`${eyebrowIcon} text-sm`}></i>}
              {eyebrow}
            </span>
          )}

          <h1 className="text-[36px] md:text-[56px] lg:text-[64px] font-semibold text-white leading-[1.05] tracking-tight max-w-3xl">
            {title}
          </h1>

          {subtitle && (
            <p className="mt-6 text-[15px] md:text-base text-white/70 leading-relaxed max-w-xl font-light">
              {subtitle}
            </p>
          )}

          {ctas.length > 0 && (
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-start gap-3">
              {ctas.map((cta, i) => {
                const base =
                  'inline-flex items-center justify-center gap-2 text-[15px] font-medium px-7 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer';
                const style =
                  cta.variant === 'secondary'
                    ? 'bg-white/10 border border-white/25 text-white hover:bg-white/20'
                    : 'bg-primary-600 text-white hover:bg-primary-700';
                const className = `${base} ${style}`;

                if (cta.to) {
                  return (
                    <Link key={i} to={cta.to} className={className}>
                      {cta.label}
                    </Link>
                  );
                }
                return (
                  <a key={i} href={cta.href} target="_blank" rel="noopener noreferrer" className={className}>
                    {cta.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}